import { Step } from "react-joyride";
import type { EvaluationPanelMember, PanelMemberStatus } from "newui/features/Rfx/Evaluation/types/panel.type";
// Render the REAL badges rather than re-declaring their colours here: MemberStatusBadge
// is documented as the single source of truth for status presentation, so the tour
// cannot drift out of sync with the table it is describing.
import { MemberStatusBadge } from "newui/features/Rfx/Evaluation/components/PanelMembers/MemberStatusBadge";
import { MemberDisclaimerTableCell } from "newui/features/Rfx/Evaluation/components/PanelMembers/MemberDisclaimerTableCell";

/** Two-column legend: real badge on the left, what it means on the right. */
const Legend = ({ rows }: { rows: { badge: React.ReactNode; text: React.ReactNode }[] }) => (
	<dl className="mt-3 space-y-2.5">
		{rows.map((row, i) => (
			<div key={i} className="grid grid-cols-[auto,1fr] items-start gap-x-3">
				<dt className="pt-0.5">{row.badge}</dt>
				<dd className="text-gray-600">{row.text}</dd>
			</div>
		))}
	</dl>
);

/** Minimal member shape for previewing a disclaimer badge pair. */
const disclaimerSample = (
	disclaimer_status: EvaluationPanelMember["disclaimer_status"],
	disclaimer_review_action?: EvaluationPanelMember["disclaimer_review_action"],
) =>
	({ disclaimer_status, disclaimer_review_action }) as EvaluationPanelMember;

const statusBadge = (status: PanelMemberStatus) => <MemberStatusBadge status={status} />;

/**
 * Guided tour steps for the Evaluation Panel step.
 *
 * Each step targets a `data-testid` attribute rendered in the step.
 *
 * Joyride does NOT auto-skip a missing target — react-joyride 2.x stalls on it, and a
 * missing FIRST target means the tour never becomes visible at all, so "Show Me" looks
 * like a dead button. (See FeatureGuide.tsx, which handles the stuck-mid-tour case.)
 * Every target here must be rendered on the *initial* view of the step, before the user
 * clicks anything — do not target an element that only appears after an interaction.
 *
 * Covered by guideTargets.test.ts (every selector exists in source) AND by
 * EvaluationPanelStep.test.tsx, which renders the step in the EMPTY state and asserts
 * each target is really in the DOM — static existence is not enough, as the
 * `panel-members-table` bug showed.
 *
 * Steps are BUILT, not static: the Disclaimer column renders only when the disclaimer
 * is enabled, and Actions only for a lead. Emitting those steps unconditionally would
 * stall the tour exactly like the two bugs above.
 */
export interface EvaluationPanelGuideOptions {
	/** Lead-only Actions column. */
	canManage: boolean;
	/** Disclaimer column — opt-in per RFx (PRO-664). */
	disclaimerEnabled: boolean;
}

export const buildEvaluationPanelSteps = ({
	canManage,
	disclaimerEnabled,
}: EvaluationPanelGuideOptions): Step[] => [
	{
		target: '[data-testid="add-panel-member-btn"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Add Panel Members</h3>
				<p>
					Build your evaluation panel here. Add a colleague from your
					organisation by searching their name, or invite an external member by
					entering their email address.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid="panel-members-section"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Panel Members</h3>
				<p>
					Your panel appears here. The rows shown are examples — they illustrate
					what you&apos;ll see once you start adding members.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid="panel-status-column-header"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-1 text-base font-semibold">Member Status</h3>
				<p className="text-gray-600">
					Each member moves through the panel in stages:
				</p>
				<Legend
					rows={[
						{
							badge: statusBadge("pending"),
							text: "Added to the panel, but no invitation sent yet.",
						},
						{
							badge: statusBadge("invited"),
							text: "Invitation email sent — awaiting their response.",
						},
						{
							badge: statusBadge("accepted"),
							text: "They've joined the panel and can begin evaluating.",
						},
					]}
				/>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	...(disclaimerEnabled
		? [
				{
					target: '[data-testid="disclaimer-column-header"]',
					content: (
						<div className="text-sm">
							<h3 className="mb-1 text-base font-semibold">
								Conflict of Interest
							</h3>
							<p className="text-gray-600">
								Members declare any conflict before they can score. A declared
								conflict shows <strong>two</strong> badges — the declaration,
								and how far your review of it has got:
							</p>
							<Legend
								rows={[
									{
										badge: <MemberDisclaimerTableCell member={disclaimerSample(null)} />,
										text: "Declaration not completed yet.",
									},
									{
										badge: (
											<MemberDisclaimerTableCell
												member={disclaimerSample("no_conflict")}
											/>
										),
										text: "Declared, with nothing to disclose.",
									},
									{
										badge: (
											<MemberDisclaimerTableCell
												member={disclaimerSample("conflict")}
											/>
										),
										text: "Conflict declared — awaiting your review.",
									},
									{
										badge: (
											<MemberDisclaimerTableCell
												member={disclaimerSample("reviewed", "accepted")}
											/>
										),
										text: "You reviewed it; the member continues scoring.",
									},
									{
										badge: (
											<MemberDisclaimerTableCell
												member={disclaimerSample("reviewed", "revoked")}
											/>
										),
										text: "You reviewed it and removed them from scoring.",
									},
								]}
							/>
						</div>
					),
					placement: "bottom" as const,
					disableBeacon: true,
				},
			]
		: []),
	...(canManage
		? [
				{
					target: '[data-testid="panel-actions-column-header"]',
					content: (
						<div className="text-sm">
							<h3 className="mb-2 text-base font-semibold">Actions</h3>
							<p>
								Manage each member from here — resend an invitation that
								hasn&apos;t been accepted, or remove someone from the panel.
							</p>
						</div>
					),
					placement: "left" as const,
					disableBeacon: true,
				},
			]
		: []),
	{
		target: '[data-testid="proceed-invitations-btn"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Send Invitations</h3>
				<p>
					Once you&apos;ve added all panel members, send invitations to the
					whole list so they can begin their evaluations. You can also continue
					without sending and invite later.
				</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
	},
];

/** Back-compat: the full step list (all columns present). */
export const evaluationPanelSteps: Step[] = buildEvaluationPanelSteps({
	canManage: true,
	disclaimerEnabled: true,
});
